import {
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  WHATSAPP_URL,
} from '../config/contact'

function primaryEmail() {
  return process.env.VUE_APP_FORM_TO_EMAIL || CONTACT_EMAIL
}

function backupEmail() {
  return process.env.VUE_APP_FORM_BACKUP_EMAIL || ''
}

export function whatsappPrefillUrl(lines) {
  const text = Array.isArray(lines) ? lines.filter(Boolean).join('\n') : String(lines || '')
  return `${WHATSAPP_URL}?text=${encodeURIComponent(text)}`
}

function leadToWhatsAppLines(subject, formName, data) {
  return [
    `Homvisit: ${subject || formName}`,
    data.name ? `Name: ${data.name}` : '',
    data.phone ? `Phone: ${data.phone}` : '',
    data.email ? `Email: ${data.email}` : '',
    data.organization ? `Organization: ${data.organization}` : '',
    data.district ? `District: ${data.district}` : '',
    data.experience ? `Experience: ${data.experience}` : '',
    data.service ? `Service: ${data.service}` : '',
    data.need ? `Need: ${data.need}` : '',
    data.preferredDate ? `Preferred date: ${data.preferredDate}` : '',
    data.location ? `Location: ${data.location}` : '',
    data.message ? `Message: ${data.message}` : '',
    data.source_page ? `Page: ${data.source_page}` : '',
  ]
}

async function postFormSubmit(toEmail, { subject, formName, data }) {
  const res = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(toEmail)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      name: data.name || 'Homvisit lead',
      email: data.email || primaryEmail(),
      phone: data.phone || '',
      message: JSON.stringify(data, null, 2),
      _subject: subject,
      _template: 'table',
      _captcha: 'false',
      form_name: formName,
      ...data,
    }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.success === 'false' || json.success === false) {
    throw new Error(json.message || `FormSubmit failed for ${toEmail}`)
  }
  return json
}

async function postWeb3Forms({ subject, formName, data }) {
  const accessKey = process.env.VUE_APP_WEB3FORMS_ACCESS_KEY
  if (!accessKey) throw new Error('No Web3Forms key')

  const res = await fetch('https://api.web3forms.com/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      access_key: accessKey,
      subject,
      from_name: formName,
      name: data.name || 'Homvisit lead',
      email: data.email || primaryEmail(),
      phone: data.phone || CONTACT_PHONE_DISPLAY,
      message: Object.entries(data)
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n'),
      ...data,
    }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.success === false) {
    throw new Error(json.message || 'Web3Forms failed')
  }
  return json
}

/**
 * 1) Always opens WhatsApp to 75 9409 9409 with lead details (guaranteed).
 * 2) Also tries Web3Forms + FormSubmit in background for email/dashboard.
 */
export async function submitForm({ subject, formName, data, openWhatsApp = true }) {
  const channels = []

  if (openWhatsApp) {
    const url = whatsappPrefillUrl(leadToWhatsAppLines(subject, formName, data))
    window.open(url, '_blank', 'noopener')
    channels.push('whatsapp')
  }

  const tries = []

  tries.push(
    postWeb3Forms({ subject, formName, data })
      .then(() => channels.push('web3forms'))
      .catch(() => null)
  )

  tries.push(
    postFormSubmit(primaryEmail(), { subject, formName, data })
      .then(() => channels.push('formsubmit'))
      .catch(() => null)
  )

  const backup = backupEmail()
  if (backup && backup.toLowerCase() !== primaryEmail().toLowerCase()) {
    tries.push(
      postFormSubmit(backup, { subject, formName, data })
        .then(() => channels.push('formsubmit_backup'))
        .catch(() => null)
    )
  }

  await Promise.all(tries)

  if (!channels.length) {
    throw new Error(
      `Could not send. Call ${CONTACT_PHONE_DISPLAY} or WhatsApp the same number.`
    )
  }

  return { ok: true, channels }
}
